import React, { Component, createRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import Hls from 'hls.js';
import { findIndex, map } from 'lodash';
import VIDEO_EVENTS from 'constants/video-events';
import AbstractPlayerInterface from './HTML5PlayerInterface';
import VideoComponent from './VideoComponent';
import apiMEGOGO from 'config/api-megogo';
import requester from 'utils/requester';
import { INIT_UI_LANGUAGE } from 'constants/languages';

class HLSPlayer extends Component {
  videoRef = createRef();
  abstractPlayerInterfaceRef = this.props.forwardedRef || createRef();

  HLSVideo = null;
  streamInfoMEGOGO = null;

  state = {
    // Need to save currentTime after lang change and restore it after MANIFEST_PARSED event
    startTimeAfterLangChange: this.props.startFrom || 0,
  };

  componentDidMount() {
    this.abstractPlayerInterfaceRef.current = new AbstractPlayerInterface(this.videoRef);
    this.onPlayerOpen();
  }

  componentWillUnmount() {
    this.destroyPlayer();
    this.abstractPlayerInterfaceRef.current = null;
  }

  componentDidUpdate(prevProps) {
    const isNewSrc = prevProps.src !== this.props.src;
    const isNewContentId = prevProps.contentId !== this.props.contentId;
    const isNewQuality = prevProps.quality !== this.props.quality;
    const isNewLang = prevProps.lang !== this.props.lang;

    if (isNewSrc || isNewContentId) this.onChangeSrc();
    if (isNewLang) this.onChangeLang();
    if (isNewQuality) this.onChangeQuality();
  }

  onChangeSrc() {
    this.destroyPlayer();
    clearTimeout(this.tickerTimerID);
    this.tickerTimerID = setTimeout(() => this.onPlayerOpen(), 500);
  }

  onChangeLang() {
    const { lang } = this.props;
    const { src } = this.streamInfoMEGOGO;
    if (!src) return;
    let srcToChange;

    if (src.indexOf('/a/') < 0) {
      srcToChange = src.replace(
        /(\/o\/[0-9]+)(\/a\/[0-9]+)?/,
        (full, objectKey) => `${objectKey}/a/${lang}`,
      );
    } else {
      srcToChange = src.replace(/\/a\/[0-9]+/, `/a/${lang}`);
    }

    this.setState({ startTimeAfterLangChange: this.videoRef.current.currentTime });
    this.HLSVideo.loadSource(srcToChange);
  }

  onChangeQuality() {
    if (!this.HLSVideo) return;
    const { quality } = this.props;
    const { levels } = this.HLSVideo;

    this.HLSVideo.currentLevel = findIndex(levels, { bitrate: quality });
  }

  async loadMegogoManifestSettings() {
    const { contentId } = this.props;
    if (!contentId?.toString().includes('MEGO')) return null;

    const assetIdForRequest = contentId.replace('MEGO', '');

    const streamInfoUrl = apiMEGOGO.getSignedUrl({
      video_id: parseInt(assetIdForRequest, 10),
      lang: INIT_UI_LANGUAGE,
    });

    const streamInfo = await requester({
      method: 'get',
      url: streamInfoUrl,
      appendParams: false,
      extendHeadersWithSession: false,
    });

    return streamInfo.data;
  }

  async onPlayerOpen() {
    const { src, startFrom, onPlayerOpen, onSettingsChange, onVideoEvent } = this.props;
    const video = this.videoRef.current;
    //Сюда может прийти либо контент MEGOGO либо трейлер от Воли
    let srcToPlay = src;

    if (!Hls.isSupported()) return;

    try {
      this.streamInfoMEGOGO = await this.loadMegogoManifestSettings();

      const { restrictions } = this.streamInfoMEGOGO || {};
      const restrictionMessages = map(restrictions, 'message').join(', ');

      if (restrictionMessages) throw new Error(restrictionMessages);
    } catch (error) {
      onVideoEvent(VIDEO_EVENTS.ERROR, error.message);
      return;
    }

    if (this.streamInfoMEGOGO && this.streamInfoMEGOGO.src) srcToPlay = this.streamInfoMEGOGO.src;

    if (this.streamInfoMEGOGO && this.streamInfoMEGOGO.audio_tracks?.length > 1) {
      const tracks = this.streamInfoMEGOGO.audio_tracks;
      const UaTrackIndex = tracks.find((track) =>
        ['uk', 'ua'].includes(track.lang?.toLowerCase()),
      ).index;

      if (UaTrackIndex) {
        srcToPlay = srcToPlay.replace(/\/a\/[0-9]+/, `/a/${UaTrackIndex}`);
      }
    }

    if (this.HLSVideo) this.HLSVideo.destroy();

    this.HLSVideo = new Hls({
      enableWorker: false,
    });
    this.HLSVideo.attachMedia(video);
    this.HLSVideo.loadSource(srcToPlay);

    this.HLSVideo.on(Hls.Events.MANIFEST_PARSED, () => {
      const { startTimeAfterLangChange } = this.state;
      const { levels } = this.HLSVideo;
      const startPosition = startTimeAfterLangChange || startFrom || 0;
      // Берём предыдущее перед максимальным качеством по индексу в массиве качеств
      this.HLSVideo.startLevel = levels.length - 2 > 0 ? levels.length - 2 : -1;
      this.HLSVideo.startLoad(startPosition);
      onSettingsChange(this.getAvailableSettings(this.HLSVideo.startLevel));
      // fix bug with lang change
      video.currentTime = startPosition;
      video.play();
    });

    this.HLSVideo.on(Hls.Events.ERROR, (_event, data) => {
      if (!data.fatal) return;
      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          // try to recover network error
          this.HLSVideo.startLoad();
          break;
        case Hls.ErrorTypes.MEDIA_ERROR:
          this.HLSVideo.recoverMediaError();
          break;
        default:
          this.HLSVideo.destroy();
          break;
      }
    });
    // HSL.js has some delay in HLSVideo.currentLevel update which causes UI bugs
    // So better to listen couple of events and pass new level in direct to setting func
    this.HLSVideo.on(Hls.Events.LEVEL_SWITCHED, (_, { level }) => {
      onSettingsChange(this.getAvailableSettings(level));
    });
    this.HLSVideo.on(Hls.Events.LEVEL_SWITCHING, (_, { level }) => {
      onSettingsChange(this.getAvailableSettings(level));
    });

    onPlayerOpen();
  }

  getAvailableSettings(currentLevel) {
    const { levels, url } = this.HLSVideo;

    let audioTracks = [];
    let bitrates = [];

    if (this.streamInfoMEGOGO) {
      audioTracks = this.streamInfoMEGOGO.audio_tracks;
      bitrates = this.streamInfoMEGOGO.bitrates;
    }

    const quality = levels.map(({ bitrate }, index) => ({
      // it's ok to set undefined as a name, it will be replaced as text by bitrate
      name: bitrates[index]?.name,
      id: bitrate,
      isActive: index === currentLevel,
    }));

    let language = audioTracks.map((track) => ({
      id: track.index,
      // only one way to check what lang is playing right now - check it from current url
      isActive: new RegExp(`/a/${track.index}`).test(url),
      name: track.display_name,
      lang: track.lang,
    }));

    const UaTrackIndex = language.findIndex((track) =>
      ['uk', 'ua'].includes(track.lang?.toLowerCase()),
    );
    language.push(...language.splice(0, UaTrackIndex));

    return { quality, language };
  }

  destroyPlayer() {
    if (!this.HLSVideo) return;
    this.HLSVideo.destroy();
    this.HLSVideo = null;
  }

  render() {
    const { contentId, width, height, onVideoEvent } = this.props;

    if (!contentId) return null;

    return (
      <VideoComponent
        width={width}
        height={height}
        onEvent={onVideoEvent}
        ref={this.videoRef}
        key={contentId}
      />
    );
  }
}

HLSPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  onPlayerOpen: PropTypes.func.isRequired,
  onSettingsChange: PropTypes.func.isRequired,
  onVideoEvent: PropTypes.func.isRequired,
  startFrom: PropTypes.number,
  quality: PropTypes.number,
  forwardedRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
};

export default forwardRef((props, ref) => <HLSPlayer forwardedRef={ref} {...props} />);
